'use client'

import {
  ArrowRight,
  Add as Plus,
  Close as X,
  Play,
  Analytics,
  Archive,
  Blockchain,
  Bus,
  ChartLine,
  ChartMultitype,
  Delivery,
  DeliveryTruck,
  DocumentTasks,
  Education,
  Enterprise,
  FlowConnection,
  Group,
  Location,
  MachineLearning,
  MachineLearningModel,
  Package,
  Partnership,
  Purchase,
  Report,
  Rule,
  Screen,
  Security,
  ShoppingCart,
  Star,
  Store,
  TaskComplete,
  UserMultiple,
  UserProfile,
  VideoChat,
  Wallet,
  type CarbonIconType,
} from '@carbon/icons-react'
import Link from 'next/link'
import { useState, use } from 'react'
import { useMounted } from '@/lib/opendocs/hooks/use-mounted'
import { Breadcrumbs } from '@/components/breadcrumbs'
import IndustryCTASection from '@/components/IndustryCTASection'
import { Button } from '@/components/ui/button'
import LogoMarquee from '@/components/logo-marquee'
import DeveloperSection from '@/components/developer-section'
import { notFound } from 'next/navigation'

interface PageProps {
  params: Promise<{
    slug: string
  }>
}

/* Capability icons. These were 36 hotlinked SVGs from a third-party vendor's
   asset CDN — six of their glyphs cycled across every industry. They are now
   local Carbon icons chosen per capability, so nothing renders from an
   external host and nothing carries another company's mark. */
const CAPABILITY_ICONS: Record<string, CarbonIconType> = {
  Analytics,
  Archive,
  Blockchain,
  Bus,
  ChartLine,
  ChartMultitype,
  Delivery,
  DeliveryTruck,
  DocumentTasks,
  Education,
  Enterprise,
  FlowConnection,
  Group,
  Location,
  MachineLearning,
  MachineLearningModel,
  Package,
  Partnership,
  Purchase,
  Report,
  Rule,
  Screen,
  Security,
  ShoppingCart,
  Star,
  Store,
  TaskComplete,
  UserMultiple,
  UserProfile,
  VideoChat,
  Wallet,
}

/* The feature panels' figure. Three variants keyed off the row index so the
   alternating panels don't repeat, drawn from the brand mint at low opacity —
   no external asset, no vendor mark, nothing to load. */
function FeatureFigure({ index }: { index: number }) {
  const MINT = '#37ecb0'
  return (
    <svg
      viewBox="0 0 320 320"
      fill="none"
      aria-hidden
      className="h-80 w-80 transition-transform duration-500 md:h-96 md:w-96 lg:h-[26rem] lg:w-[26rem]"
    >
      {index % 3 === 0 &&
        // concentric rings
        [56, 88, 120, 152].map((r, i) => (
          <circle
            key={r}
            cx="160"
            cy="160"
            r={r}
            stroke={MINT}
            strokeOpacity={0.42 - i * 0.09}
            strokeWidth={1.4 - i * 0.25}
          />
        ))}

      {index % 3 === 1 &&
        // nested squares, rotated
        [70, 104, 138, 172].map((s, i) => (
          <rect
            key={s}
            x={160 - s / 2}
            y={160 - s / 2}
            width={s}
            height={s}
            rx={10}
            transform={`rotate(${i * 11} 160 160)`}
            stroke={MINT}
            strokeOpacity={0.4 - i * 0.085}
            strokeWidth={1.4 - i * 0.25}
          />
        ))}

      {index % 3 === 2 && (
        // a rising arc field
        <>
          {[0, 1, 2, 3, 4].map((i) => (
            <path
              key={i}
              d={`M40 ${268 - i * 18} Q 160 ${188 - i * 34} 280 ${268 - i * 18}`}
              stroke={MINT}
              strokeOpacity={0.4 - i * 0.06}
              strokeWidth={1.3 - i * 0.16}
            />
          ))}
          <circle cx="160" cy="150" r="9" fill={MINT} fillOpacity="0.85" />
        </>
      )}
    </svg>
  )
}

function CapabilityIcon({ name }: { name: string }) {
  const Glyph = CAPABILITY_ICONS[name] ?? Enterprise
  return (
    <Glyph
      size={40}
      aria-hidden
      className="text-emerald-600 dark:text-primary-active"
    />
  )
}

const industriesContent = {
  education: {
    title: 'Euclideum for Edtech',
    /* `heading` is the page h1; `title` stays the short label the media
       captions read, so the headline can name the subject in full without
       turning every alt string into a sentence. */
    heading: 'Education Management Solutions for Schools and Colleges',
    heroDescription:
      'Transform learning with intelligent platforms, student management systems, and AI-powered solutions that elevate educational outcomes for modern institutions.',
    heroImage: '/illustrations/lms.webp',
    videoId: 'dQw4w9WgXcQ',
    sectionTitle: 'Delivering modern, innovative\neducation solutions',
    features: [
      {
        title: 'Smart Learning\nManagement',
        description:
          'Our comprehensive LMS platform empowers educators and engages students with intuitive course management, real-time collaboration, and powerful analytics.',
        linkText: 'Explore LMS features',
      },
      {
        title: 'AI-Powered\nPersonalization',
        description:
          "Leverage artificial intelligence to create adaptive learning experiences, intelligent content recommendations, and automated assessment that meets each student's unique needs.",
        linkText: 'Discover AI capabilities',
      },
      {
        title: 'Data-Driven\nInsights',
        description:
          'Transform educational data into actionable insights. Track student progress, measure learning outcomes, and optimize teaching strategies with comprehensive analytics.',
        linkText: 'View analytics solutions',
      },
    ],
    ctaTitle: 'Education Platform Modernization',
    ctaDescription:
      'Unlock innovation in education technology. Our platform provides a faster, predictable path to modernization for educational institutions worldwide.',
    capabilitiesTitle: 'Education technology\nsolutions',
    capabilitiesDescription:
      'Comprehensive platforms that empower institutions to deliver exceptional learning experiences',
    capabilities: [
      {
        id: 1,
        title: 'Learning Management\nSystems',
        description:
          'Build comprehensive LMS platforms with advanced course management, student tracking, and assessment tools. Deliver personalized learning experiences at scale.',
        icon: 'Education',
        linkText: 'Explore LMS',
      },
      {
        id: 2,
        title: 'Student Information\nSystems',
        description:
          "Manage student records, attendance, grades, and academic progress with flexible data models that adapt to your institution's unique requirements.",
        icon: 'Group',
        linkText: 'Learn more',
      },
      {
        id: 3,
        title: 'AI-Powered Learning',
        description:
          'Leverage AI to create adaptive learning paths, intelligent tutoring systems, and automated grading that enhances educational outcomes.',
        icon: 'MachineLearningModel',
        linkText: 'Discover AI solutions',
      },
      {
        id: 4,
        title: 'Analytics & Insights',
        description:
          'Transform educational data into actionable insights. Track student performance, identify at-risk students, and optimize curriculum effectiveness.',
        icon: 'Analytics',
        linkText: 'View analytics',
      },
      {
        id: 5,
        title: 'Virtual Classrooms',
        description:
          'Enable seamless remote learning with integrated video conferencing, real-time collaboration tools, and interactive content delivery systems.',
        icon: 'Screen',
        linkText: 'Start teaching online',
      },
      {
        id: 6,
        title: 'Assessment & Testing',
        description:
          'Create and deliver secure online assessments with anti-cheating measures, automated grading, and comprehensive performance analytics.',
        icon: 'Report',
        linkText: 'Assessment tools',
      },
    ],
    faqs: [
      {
        id: 1,
        question: 'How can Euclideum transform our educational institution?',
        answer:
          'Euclideum provides comprehensive education technology solutions including LMS platforms, student management systems, AI-powered learning tools, and analytics to modernize your institution and enhance learning outcomes.',
      },
      {
        id: 2,
        question: "What makes Euclideum's LMS different from others?",
        answer:
          'Our LMS is built with modern technology stack, offering superior flexibility, scalability, and integration capabilities. It supports personalized learning paths, advanced analytics, and seamless third-party integrations.',
      },
      {
        id: 3,
        question: 'Can you integrate with our existing systems?',
        answer:
          'Yes, our solutions are designed for seamless integration with existing student information systems, HR systems, payment gateways, and other educational tools through robust APIs.',
      },
      {
        id: 4,
        question: 'How do you ensure data security and privacy?',
        answer:
          'We implement industry-leading security measures including encryption, secure authentication, role-based access control, and compliance with educational data privacy regulations like FERPA and GDPR.',
      },
      {
        id: 5,
        question: 'What kind of support and training do you provide?',
        answer:
          'We offer comprehensive onboarding, training programs for administrators and educators, detailed documentation, and ongoing technical support to ensure successful implementation and adoption.',
      },
      {
        id: 6,
        question: 'Can your solutions scale with our growing institution?',
        answer:
          'Absolutely. Our cloud-based architecture is designed to scale seamlessly from small institutions to large universities, handling millions of users without performance degradation.',
      },
    ],
  },
  logistics: {
    title: 'Euclideum for Logistics',
    heading: 'Logistics and Supply Chain Software',
    heroDescription:
      'Optimize supply chain operations with intelligent logistics solutions through fleet management systems, route optimization tools, and AI-powered platforms that deliver efficiency and cost savings.',
    heroImage: '/illustrations/intelqi_udc.webp',
    videoId: 'dQw4w9WgXcQ',
    sectionTitle: 'Delivering intelligent\nlogistics solutions',
    features: [
      {
        title: 'Smart Fleet\nManagement',
        description:
          'Monitor and manage your entire fleet in real-time with GPS tracking, predictive maintenance, fuel optimization, and comprehensive driver management systems.',
        linkText: 'Manage your fleet',
      },
      {
        title: 'AI-Powered\nRoute Optimization',
        description:
          'Reduce costs and improve delivery times with intelligent route planning that considers traffic, weather, delivery windows, and vehicle capacity in real-time.',
        linkText: 'Optimize routes',
      },
      {
        title: 'End-to-End\nVisibility',
        description:
          'Gain complete supply chain visibility with real-time shipment tracking, automated notifications, and comprehensive analytics across your entire logistics network.',
        linkText: 'Track shipments',
      },
    ],
    ctaTitle: 'Logistics Platform Modernization',
    ctaDescription:
      'Unlock innovation in logistics technology. Our platform provides a faster, predictable path to modernization for freight companies, warehouses, and supply chain operations worldwide.',
    capabilitiesTitle: 'Logistics technology\nsolutions',
    capabilitiesDescription:
      'Comprehensive platforms that optimize operations and reduce costs',
    capabilities: [
      {
        id: 1,
        title: 'Fleet Management',
        description:
          'Comprehensive fleet tracking with real-time GPS monitoring, vehicle maintenance scheduling, fuel management, and driver performance analytics.',
        icon: 'DeliveryTruck',
        linkText: 'Manage fleet',
      },
      {
        id: 2,
        title: 'Route Optimization',
        description:
          'AI-powered route planning that minimizes delivery times, reduces fuel costs, and maximizes fleet utilization with dynamic re-routing capabilities.',
        icon: 'Location',
        linkText: 'Optimize routes',
      },
      {
        id: 3,
        title: 'Warehouse Management',
        description:
          'Streamline warehouse operations with inventory tracking, automated picking systems, dock management, and real-time stock visibility.',
        icon: 'Package',
        linkText: 'WMS solutions',
      },
      {
        id: 4,
        title: 'Shipment Tracking',
        description:
          'End-to-end shipment visibility with real-time tracking, automated notifications, proof of delivery, and customer self-service portals.',
        icon: 'Delivery',
        linkText: 'Track shipments',
      },
      {
        id: 5,
        title: 'Supply Chain Analytics',
        description:
          'Gain insights into logistics operations with predictive analytics, performance dashboards, and data-driven decision making tools.',
        icon: 'ChartLine',
        linkText: 'View analytics',
      },
      {
        id: 6,
        title: 'Last-Mile Delivery',
        description:
          'Optimize last-mile operations with delivery management, route optimization, real-time tracking, and customer communication tools.',
        icon: 'Bus',
        linkText: 'Delivery solutions',
      },
    ],
    faqs: [
      {
        id: 1,
        question:
          'How can your logistics solutions improve delivery efficiency?',
        answer:
          'Our solutions use AI-powered route optimization, real-time traffic data, and predictive analytics to reduce delivery times by up to 30%, minimize fuel costs, and improve fleet utilization.',
      },
      {
        id: 2,
        question: 'Can you integrate with existing TMS and WMS systems?',
        answer:
          'Yes, we provide seamless integration with existing Transportation Management Systems (TMS), Warehouse Management Systems (WMS), ERP systems, and third-party logistics providers through robust APIs.',
      },
      {
        id: 3,
        question: 'How do you handle real-time tracking and visibility?',
        answer:
          'We implement GPS tracking, IoT sensors, and mobile apps to provide real-time visibility of shipments, vehicles, and inventory. Customers and stakeholders can track shipments through web portals or mobile apps.',
      },
      {
        id: 4,
        question: 'What technologies do you use for route optimization?',
        answer:
          'We leverage machine learning algorithms, historical data analysis, real-time traffic information, weather data, and delivery constraints to generate optimal routes that adapt dynamically to changing conditions.',
      },
      {
        id: 5,
        question: 'Can your solutions scale for large logistics operations?',
        answer:
          'Absolutely. Our cloud-based architecture scales seamlessly to handle thousands of vehicles, millions of shipments, and complex multi-warehouse operations across multiple regions.',
      },
      {
        id: 6,
        question: 'How do you ensure data security in logistics operations?',
        answer:
          'We implement end-to-end encryption, secure authentication, role-based access control, and compliance with industry standards to protect sensitive logistics data and customer information.',
      },
    ],
  },
  retail: {
    title: 'Euclideum for Retail',
    heading: 'Retail and E-Commerce Software',
    heroDescription:
      'Transform retail experiences with modern e-commerce platforms, omnichannel solutions, and intelligent inventory systems that drive sales and customer loyalty.',
    heroImage: '/illustrations/payroll.webp',
    videoId: 'dQw4w9WgXcQ',
    sectionTitle: 'Delivering modern,\nseamless retail solutions',
    features: [
      {
        title: 'Next-Generation\nE-Commerce',
        description:
          'Create stunning online stores with intuitive interfaces, lightning-fast performance, and conversion-optimized checkout experiences that turn visitors into customers.',
        linkText: 'Launch your store',
      },
      {
        title: 'Unified\nOmnichannel',
        description:
          'Seamlessly integrate online and offline channels. Provide consistent experiences with unified inventory, customer profiles, and sales data across all touchpoints.',
        linkText: 'Go omnichannel',
      },
      {
        title: 'Smart Inventory\nManagement',
        description:
          'Optimize stock levels with AI-powered demand forecasting, automated reordering, and real-time tracking across multiple warehouses and retail locations.',
        linkText: 'Manage inventory',
      },
    ],
    ctaTitle: 'Retail Platform Modernization',
    ctaDescription:
      'Unlock innovation in retail technology. Our platform provides a faster, predictable path to modernization for retailers, e-commerce businesses, and omnichannel brands worldwide.',
    capabilitiesTitle: 'Retail technology\nsolutions',
    capabilitiesDescription:
      'Comprehensive platforms that drive sales and enhance customer experiences',
    capabilities: [
      {
        id: 1,
        title: 'E-Commerce\nPlatforms',
        description:
          'Build powerful online stores with seamless shopping experiences, multi-vendor support, advanced search, personalized recommendations, and secure checkout.',
        icon: 'ShoppingCart',
        linkText: 'Build your store',
      },
      {
        id: 2,
        title: 'Omnichannel Retail',
        description:
          'Unify online and offline experiences with integrated POS systems, inventory management, and customer data across all touchpoints.',
        icon: 'Store',
        linkText: 'Go omnichannel',
      },
      {
        id: 3,
        title: 'Inventory Management',
        description:
          'Real-time inventory tracking, automated reordering, warehouse management, and multi-location stock synchronization for optimal operations.',
        icon: 'Package',
        linkText: 'Manage inventory',
      },
      {
        id: 4,
        title: 'Customer Analytics',
        description:
          'Understand customer behavior, predict trends, personalize marketing, and optimize pricing with advanced retail analytics and AI insights.',
        icon: 'UserMultiple',
        linkText: 'View analytics',
      },
      {
        id: 5,
        title: 'Loyalty Programs',
        description:
          'Engage customers with points-based rewards, tiered memberships, personalized offers, and gamification features that drive repeat purchases.',
        icon: 'Star',
        linkText: 'Build loyalty',
      },
      {
        id: 6,
        title: 'Supply Chain',
        description:
          'Optimize supply chain operations with vendor management, demand forecasting, procurement automation, and real-time shipment tracking.',
        icon: 'FlowConnection',
        linkText: 'Optimize supply chain',
      },
    ],
    faqs: [
      {
        id: 1,
        question: 'How quickly can you launch an e-commerce platform?',
        answer:
          'Our modular approach enables rapid deployment. A basic e-commerce platform can be launched in 4-6 weeks, while more complex solutions with custom features typically take 2-4 months depending on requirements.',
      },
      {
        id: 2,
        question: 'Can you integrate with existing retail systems?',
        answer:
          'Yes, we can integrate with existing ERP, CRM, POS, payment gateways, shipping providers, and accounting systems through robust APIs and middleware solutions.',
      },
      {
        id: 3,
        question: 'How do you handle peak traffic during sales events?',
        answer:
          'Our cloud-based infrastructure automatically scales to handle traffic spikes. We implement caching, CDN, load balancing, and database optimization to ensure smooth performance during high-demand periods.',
      },
      {
        id: 4,
        question: 'What payment methods and currencies do you support?',
        answer:
          'We support all major payment gateways, multiple currencies, various payment methods including cards, digital wallets, UPI, buy-now-pay-later, and cryptocurrency payments.',
      },
      {
        id: 5,
        question: 'Can you build mobile apps for our retail business?',
        answer:
          'Absolutely. We develop native iOS and Android apps, as well as progressive web apps (PWAs) that provide seamless shopping experiences with features like push notifications, mobile payments, and AR try-on.',
      },
      {
        id: 6,
        question: 'How do you ensure data security for customer information?',
        answer:
          'We implement industry-standard security measures including SSL encryption, PCI-DSS compliance, secure payment processing, fraud detection, and regular security audits to protect customer data.',
      },
    ],
  },
  fintech: {
    title: 'Euclideum for Fintech',
    heading: 'Fintech and Digital Banking Software',
    heroDescription:
      'Power the future of finance with secure, scalable fintech solutions that transform how people manage money through digital banking, payments, and AI-driven services.',
    heroImage: '/illustrations/accounting.webp',
    videoId: 'dQw4w9WgXcQ',
    sectionTitle: 'Delivering secure, innovative\nfinancial technology',
    features: [
      {
        title: 'Next-Gen Digital\nBanking',
        description:
          'Build comprehensive digital banking platforms with real-time transactions, multi-currency support, and seamless customer experiences across web and mobile.',
        linkText: 'Explore banking solutions',
      },
      {
        title: 'AI-Powered\nFraud Prevention',
        description:
          'Protect your customers with advanced AI and machine learning algorithms that detect and prevent fraudulent activities in real-time with minimal false positives.',
        linkText: 'Learn about security',
      },
      {
        title: 'Seamless Payment\nProcessing',
        description:
          'Handle millions of transactions daily with our robust payment infrastructure supporting multiple payment methods, currencies, and instant settlement capabilities.',
        linkText: 'View payment solutions',
      },
    ],
    ctaTitle: 'Fintech Platform Modernization',
    ctaDescription:
      'Unlock innovation in financial technology. Our platform provides a faster, predictable path to modernization for banks, fintechs, and financial institutions worldwide.',
    capabilitiesTitle: 'Fintech solutions\nfor tomorrow',
    capabilitiesDescription:
      'Comprehensive financial technology platforms that drive innovation and growth',
    capabilities: [
      {
        id: 1,
        title: 'Digital Banking\nPlatforms',
        description:
          'Build secure, scalable digital banking solutions with real-time transactions, account management, and seamless customer experiences across all channels.',
        icon: 'Wallet',
        linkText: 'Explore banking',
      },
      {
        id: 2,
        title: 'Payment Processing',
        description:
          'Handle high-volume payment transactions with lightning-fast processing, multiple payment methods, and robust security protocols for safe transactions.',
        icon: 'Purchase',
        linkText: 'Payment solutions',
      },
      {
        id: 3,
        title: 'Fraud Detection',
        description:
          'Implement AI-powered fraud detection systems that identify suspicious activities in real-time, protecting your customers and reducing financial losses.',
        icon: 'Security',
        linkText: 'Security systems',
      },
      {
        id: 4,
        title: 'Blockchain Integration',
        description:
          'Leverage blockchain technology for secure transactions, smart contracts, and decentralized finance solutions that meet modern fintech demands.',
        icon: 'Blockchain',
        linkText: 'Blockchain solutions',
      },
      {
        id: 5,
        title: 'Regulatory Compliance',
        description:
          'Ensure compliance with financial regulations including KYC, AML, PCI-DSS, and GDPR through automated monitoring and reporting systems.',
        icon: 'Rule',
        linkText: 'Compliance tools',
      },
      {
        id: 6,
        title: 'Analytics & Reporting',
        description:
          'Transform financial data into actionable insights with advanced analytics, risk assessment, and comprehensive reporting dashboards.',
        icon: 'Report',
        linkText: 'View analytics',
      },
    ],
    faqs: [
      {
        id: 1,
        question:
          'How does Euclideum ensure security for fintech applications?',
        answer:
          'We implement industry-leading security measures including end-to-end encryption, multi-factor authentication, secure APIs, regular security audits, and compliance with PCI-DSS, SOC 2, and other financial security standards.',
      },
      {
        id: 2,
        question: 'Can you handle high-volume transaction processing?',
        answer:
          'Yes, our systems are built for scale, capable of processing millions of transactions per day with low latency, high availability, and automatic failover mechanisms to ensure uninterrupted service.',
      },
      {
        id: 3,
        question: 'How do you ensure regulatory compliance?',
        answer:
          'We build compliance directly into our solutions, supporting KYC, AML, PCI-DSS, GDPR, and other regulatory requirements through automated workflows, audit trails, and comprehensive reporting capabilities.',
      },
      {
        id: 4,
        question: 'What payment methods and gateways do you support?',
        answer:
          'Our payment solutions integrate with all major payment gateways and support various payment methods including credit/debit cards, digital wallets, UPI, net banking, and cryptocurrency payments.',
      },
      {
        id: 5,
        question: 'How quickly can you deploy a fintech solution?',
        answer:
          'Deployment timelines vary based on complexity, but our modular architecture and proven methodologies enable rapid implementation, typically ranging from a few weeks for basic solutions to several months for complex platforms.',
      },
      {
        id: 6,
        question: 'Do you provide ongoing support and maintenance?',
        answer:
          'Yes, we offer comprehensive support packages including 24/7 monitoring, regular updates, security patches, performance optimization, and dedicated technical support to ensure your platform runs smoothly.',
      },
    ],
  },
  healthcare: {
    title: 'Euclideum for Healthcare',
    heading: 'Healthcare Technology Solutions',
    heroDescription:
      'Transform patient care with cutting-edge healthcare technology through secure EHR systems, telemedicine platforms, and AI-powered diagnostic tools that improve outcomes and save lives.',
    heroImage: '/illustrations/hrms.webp',
    videoId: 'dQw4w9WgXcQ',
    sectionTitle: 'Delivering innovative\nhealthcare technology',
    features: [
      {
        title: 'Comprehensive\nEHR Systems',
        description:
          'Streamline healthcare operations with integrated electronic health records that centralize patient data, improve care coordination, and enhance clinical workflows.',
        linkText: 'Explore EHR solutions',
      },
      {
        title: 'AI-Powered\nDiagnostics',
        description:
          'Leverage artificial intelligence for early disease detection, medical image analysis, and clinical decision support that enhances diagnostic accuracy.',
        linkText: 'AI diagnostic tools',
      },
      {
        title: 'Secure\nTelemedicine',
        description:
          'Expand healthcare access with HIPAA-compliant telemedicine platforms featuring video consultations, e-prescriptions, and seamless EHR integration.',
        linkText: 'Telehealth platform',
      },
    ],
    ctaTitle: 'Healthcare Platform Modernization',
    ctaDescription:
      'Unlock innovation in healthcare technology. Our platform provides a faster, predictable path to modernization for hospitals, clinics, and healthcare providers worldwide.',
    capabilitiesTitle: 'Healthcare technology\nsolutions',
    capabilitiesDescription:
      'Comprehensive platforms that improve patient care and operational efficiency',
    capabilities: [
      {
        id: 1,
        title: 'Electronic Health\nRecords (EHR)',
        description:
          'Comprehensive EHR systems with secure patient data management, interoperability with other systems, and compliance with healthcare regulations like HIPAA.',
        icon: 'DocumentTasks',
        linkText: 'Explore EHR',
      },
      {
        id: 2,
        title: 'Telemedicine\nPlatforms',
        description:
          'Enable remote healthcare delivery with video consultations, prescription management, appointment scheduling, and secure patient communication.',
        icon: 'VideoChat',
        linkText: 'Telehealth solutions',
      },
      {
        id: 3,
        title: 'Healthcare Analytics',
        description:
          'Transform healthcare data into actionable insights for better patient outcomes, operational efficiency, and evidence-based decision making.',
        icon: 'ChartLine',
        linkText: 'View analytics',
      },
      {
        id: 4,
        title: 'AI Diagnostics',
        description:
          'Leverage AI and machine learning for early disease detection, medical image analysis, and clinical decision support systems.',
        icon: 'MachineLearning',
        linkText: 'AI capabilities',
      },
      {
        id: 5,
        title: 'Patient Portal',
        description:
          'Empower patients with access to their health records, test results, appointment booking, prescription refills, and secure messaging with providers.',
        icon: 'UserProfile',
        linkText: 'Patient engagement',
      },
      {
        id: 6,
        title: 'HIPAA Compliance',
        description:
          'Built-in security and compliance features ensuring protection of patient data and adherence to HIPAA, GDPR, and other healthcare regulations.',
        icon: 'Security',
        linkText: 'Security & compliance',
      },
    ],
    faqs: [
      {
        id: 1,
        question:
          'How do you ensure HIPAA compliance in your healthcare solutions?',
        answer:
          'We implement comprehensive security measures including end-to-end encryption, access controls, audit logs, data backup, and disaster recovery. Our systems are designed from the ground up to meet HIPAA requirements for protecting patient health information.',
      },
      {
        id: 2,
        question:
          'Can your systems integrate with existing healthcare software?',
        answer:
          'Yes, our solutions support standard healthcare interoperability protocols like HL7, FHIR, and DICOM. We can integrate with existing EHR systems, laboratory systems, pharmacy systems, and other healthcare IT infrastructure.',
      },
      {
        id: 3,
        question: 'What telemedicine features do you provide?',
        answer:
          'Our telemedicine platform includes HD video consultations, appointment scheduling, prescription management, medical record access, secure messaging, payment processing, and integration with electronic health records.',
      },
      {
        id: 4,
        question: 'How do you handle medical image storage and analysis?',
        answer:
          'We provide secure, DICOM-compliant storage for medical images with AI-powered analysis capabilities. Our systems support radiology, pathology, and other imaging workflows with fast retrieval and advanced visualization tools.',
      },
      {
        id: 5,
        question:
          'Can you build custom healthcare applications for specific needs?',
        answer:
          "Absolutely. We specialize in building custom healthcare solutions tailored to your specific requirements, whether it's for hospitals, clinics, diagnostic centers, or specialized medical practices.",
      },
      {
        id: 6,
        question: 'What kind of support do you provide post-implementation?',
        answer:
          'We offer comprehensive support including 24/7 monitoring, regular security updates, system maintenance, user training, and dedicated technical support to ensure your healthcare operations run smoothly.',
      },
    ],
  },
  corporate: {
    title: 'Euclideum for Corporate',
    heading: 'Corporate and Enterprise Software',
    heroDescription:
      'Empower your enterprise with comprehensive business solutions through integrated ERP systems, HRMS platforms, and intelligent automation tools that drive operational excellence and growth.',
    heroImage: '/illustrations/accounting.webp',
    videoId: 'dQw4w9WgXcQ',
    sectionTitle: 'Delivering enterprise-grade\nbusiness solutions',
    features: [
      {
        title: 'Integrated ERP\nSolutions',
        description:
          'Unify your business operations with comprehensive ERP systems that connect finance, operations, supply chain, and human resources in one powerful platform.',
        linkText: 'Explore ERP solutions',
      },
      {
        title: 'Intelligent\nAutomation',
        description:
          'Leverage AI and RPA to automate repetitive tasks, streamline workflows, and free your team to focus on strategic initiatives that drive business value.',
        linkText: 'Automation solutions',
      },
      {
        title: 'Data-Driven\nDecision Making',
        description:
          'Transform business data into actionable insights with advanced BI tools, custom dashboards, and predictive analytics that empower strategic decisions.',
        linkText: 'Business intelligence',
      },
    ],
    ctaTitle: 'Corporate Platform Modernization',
    ctaDescription:
      'Unlock innovation in enterprise technology. Our platform provides a faster, predictable path to modernization for corporations, enterprises, and organizations worldwide.',
    capabilitiesTitle: 'Enterprise solutions\nfor modern business',
    capabilitiesDescription:
      'Comprehensive platforms that streamline operations and accelerate growth',
    capabilities: [
      {
        id: 1,
        title: 'Enterprise Resource\nPlanning (ERP)',
        description:
          'Unified ERP systems that integrate finance, HR, operations, and supply chain. Streamline business processes and gain real-time visibility across your organization.',
        icon: 'Enterprise',
        linkText: 'Explore ERP',
      },
      {
        id: 2,
        title: 'Human Resource\nManagement (HRMS)',
        description:
          'Comprehensive HRMS with payroll, attendance, performance management, recruitment, and employee self-service portals for modern workforce management.',
        icon: 'UserMultiple',
        linkText: 'HRMS solutions',
      },
      {
        id: 3,
        title: 'Document Management',
        description:
          'Secure document management systems with version control, approval workflows, digital signatures, and compliance tracking for paperless operations.',
        icon: 'Archive',
        linkText: 'Manage documents',
      },
      {
        id: 4,
        title: 'Business Intelligence',
        description:
          'Transform data into insights with advanced analytics dashboards, custom reports, predictive analytics, and data visualization tools.',
        icon: 'ChartMultitype',
        linkText: 'View BI tools',
      },
      {
        id: 5,
        title: 'Project Management',
        description:
          'Collaborative project management platforms with task tracking, resource allocation, time tracking, and portfolio management capabilities.',
        icon: 'TaskComplete',
        linkText: 'Manage projects',
      },
      {
        id: 6,
        title: 'CRM Systems',
        description:
          'Customer relationship management solutions that streamline sales, marketing, and customer service with 360-degree customer views.',
        icon: 'Partnership',
        linkText: 'CRM platform',
      },
    ],
    faqs: [
      {
        id: 1,
        question: 'How can Euclideum help modernize our corporate systems?',
        answer:
          'We specialize in digital transformation of enterprise systems. Our solutions replace legacy software with modern, cloud-based platforms that improve efficiency, reduce costs, and enable data-driven decision making.',
      },
      {
        id: 2,
        question:
          'Can you customize solutions for our specific business processes?',
        answer:
          "Absolutely. We don't believe in one-size-fits-all. Our team works closely with you to understand your unique workflows and build customized solutions that align perfectly with your business requirements.",
      },
      {
        id: 3,
        question: 'How do you handle data migration from legacy systems?',
        answer:
          'We follow a structured approach to data migration including data assessment, cleaning, mapping, and validation. Our process ensures zero data loss and minimal downtime during transition.',
      },
      {
        id: 4,
        question:
          'What is the typical implementation timeline for ERP systems?',
        answer:
          'Implementation timelines vary based on scope and complexity. A basic ERP deployment typically takes 3-6 months, while comprehensive enterprise-wide implementations may take 6-12 months with phased rollouts.',
      },
      {
        id: 5,
        question: 'Do you provide training and change management support?',
        answer:
          'Yes, we offer comprehensive training programs, detailed documentation, and change management support to ensure smooth adoption. We also provide train-the-trainer programs for ongoing knowledge transfer.',
      },
      {
        id: 6,
        question: 'How do you ensure system security and compliance?',
        answer:
          'We implement multi-layered security including encryption, access controls, audit trails, and regular security assessments. Our solutions comply with industry standards like ISO 27001, SOC 2, and GDPR.',
      },
    ],
  },
}

export default function IndustryPage(props: PageProps) {
  const params = use(props.params)
  const industry =
    industriesContent[params.slug as keyof typeof industriesContent]
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [isVideoOpen, setIsVideoOpen] = useState(false)
  const mounted = useMounted()

  const toggleFaq = (id: number) => {
    setOpenFaq(openFaq === id ? null : id)
  }

  if (!industry) {
    notFound()
  }

  return (
    <main className="relative bg-background">
      <section id="overview" className="relative hero-adaptive-bg scroll-mt-20">
        <div className="relative py-10 lg:py-14 overflow-hidden">
          <div className="container px-6 lg:px-8">
            <div className="mx-auto max-w-7xl">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center relative z-10">
                <div className="space-y-6">
                  {/* Every industry title is authored as "Euclideum for X"; the
                      crumb wants the sector on its own, so the prefix is
                      dropped rather than a second name being invented. */}
                  <Breadcrumbs
                    items={[
                      { name: 'Home', href: '/' },
                      { name: 'Industries', href: '/industries' },
                      {
                        name: industry.title.replace(/^Euclideum for /, ''),
                        href: `/industries/${params.slug}`,
                      },
                    ]}
                  />

                  <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-normal text-foreground leading-tight animate-fade-in">
                    {industry.heading}
                  </h1>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed animate-fade-in-delay">
                    {industry.heroDescription}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 pt-2">
                    <Button asChild size="xl">
                      <Link href="#">View Product Demo</Link>
                    </Button>
                    <Button asChild variant="outline" size="xl">
                      <Link href="#">Download Brochure</Link>
                    </Button>
                  </div>
                </div>
                <div className="animate-scale-in">
                  <div
                    className="relative cursor-pointer group"
                    onClick={() => setIsVideoOpen(true)}
                  >
                    <img
                      src={industry.heroImage}
                      alt={`${industry.title} Video`}
                      className="w-full h-auto rounded-xl shadow-2xl"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-emerald-600 dark:bg-primary-active rounded-full p-4 shadow-xl transition-all duration-300">
                        <Play className="w-8 h-8 text-primary-foreground fill-primary-foreground transition-all duration-300 group-hover:drop-shadow-[0_0_8px_rgba(20,20,23,0.8)]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background py-12 lg:py-16">
        <div className="container px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="text-center mb-10 lg:mb-14">
              <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-normal text-foreground leading-tight whitespace-pre-line">
                {industry.sectionTitle}
              </h2>
            </div>

            {industry.features.map((feature, index) => (
              <div
                key={index}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center justify-between gap-8 lg:gap-12 ${index < industry.features.length - 1 ? 'mb-12 lg:mb-16' : ''} card-hover-parent`}
              >
                <div className="max-w-2xl lg:w-1/2">
                  <h3 className="text-3xl md:text-4xl lg:text-5xl font-heading font-normal mb-6 leading-tight text-foreground whitespace-pre-line">
                    {feature.title}
                  </h3>
                  <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-6">
                    {feature.description}
                  </p>
                  <Link
                    href="#"
                    className="inline-flex items-center gap-2 text-foreground hover:text-emerald-600 dark:hover:text-primary-active font-medium text-lg transition-all group"
                  >
                    <span>{feature.linkText}</span>
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
                {/* Three spot illustrations used to be hotlinked here from a
                    third-party vendor's CMS — the same array on every industry,
                    one of them a partner's co-marketing artwork carrying their
                    mark. Replaced with a drawn figure that takes its geometry
                    from the feature's index, so the three panels still differ
                    without borrowing anyone's assets. */}
                <div
                  className={`relative flex-shrink-0 lg:w-1/2 flex ${index % 2 === 0 ? 'justify-end' : 'justify-start'} card-hover-image`}
                >
                  <FeatureFigure index={index} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <IndustryCTASection
        title={industry.ctaTitle}
        description={industry.ctaDescription}
        buttonText="Experience Product Demo"
        buttonLink="#"
      />

      <section
        id="capabilities"
        className="bg-background py-12 lg:py-16 scroll-mt-20"
      >
        <div className="container px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="text-center mb-12 lg:mb-16">
              <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-normal text-foreground leading-tight mb-4 whitespace-pre-line">
                {industry.capabilitiesTitle}
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                {industry.capabilitiesDescription}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industry.capabilities.map((card) => (
                <div
                  key={card.id}
                  className="bg-card rounded-[32px] p-8 border border-border hover:shadow-xl hover:border-emerald-600/30 dark:hover:border-primary-active/30 transition-all duration-300 capability-card"
                >
                  <div className="mb-6 flex h-10 items-center">
                    <CapabilityIcon name={card.icon} />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-4 whitespace-pre-line">
                    {card.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-8 min-h-[100px]">
                    {card.description}
                  </p>
                  <Link
                    href="#"
                    className="inline-flex items-center gap-2 text-foreground hover:text-emerald-600 dark:hover:text-primary-active font-semibold text-base transition-colors group"
                  >
                    <span>{card.linkText}</span>
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background">
        <DeveloperSection />
      </section>
      <section className="bg-background py-10 lg:py-12">
        <LogoMarquee />
      </section>

      <section id="faq" className="bg-background py-12 lg:py-16 scroll-mt-20">
        <div className="container px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <div className="text-center mb-10 lg:mb-14">
              <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-normal text-foreground leading-tight">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="divide-y divide-border border-t border-border">
              {industry.faqs.map((faq) => (
                <div key={faq.id} className="py-5">
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    className="flex w-full items-center justify-between text-left group"
                  >
                    <h3 className="text-lg md:text-xl font-semibold text-foreground pr-8 group-hover:text-emerald-600 dark:group-hover:text-primary-active transition-colors">
                      {faq.question}
                    </h3>
                    <Plus
                      className={`h-6 w-6 flex-shrink-0 text-emerald-600 dark:text-primary-active transition-transform duration-300 ${openFaq === faq.id ? 'rotate-45' : ''}`}
                    />
                  </button>
                  {openFaq === faq.id && (
                    <div className="mt-4 pr-12 animate-fade-in">
                      <p className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <style
        dangerouslySetInnerHTML={{
          __html: `@keyframes fade-in{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}@keyframes scale-in{from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}}.animate-fade-in{animation:fade-in .6s ease-out}.animate-fade-in-delay{animation:fade-in .6s ease-out .2s both}.animate-scale-in{animation:scale-in .7s ease-out .3s both}.card-hover-parent:hover .card-hover-image img{transform:scale(1.05) rotate(2deg)}.capability-card{background:hsl(var(--card))}.capability-card:hover{background:hsl(var(--accent))}`,
        }}
      />

      {mounted && isVideoOpen && (
        <div
          className="fixed inset-0 z-[9999] bg-gradient-to-br from-black/95 via-black/95 to-black/95 backdrop-blur-2xl flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setIsVideoOpen(false)}
        >
          <button
            onClick={() => setIsVideoOpen(false)}
            className="absolute top-6 right-6 z-[10000] bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full p-3 transition-all duration-300 group"
            aria-label="Close video"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div
            className="relative w-[90%] h-[85vh] max-w-[1600px] animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full h-full rounded-2xl overflow-hidden shadow-[0_0_100px_hsl(var(--primary-active)/0.3)] border-2 border-primary-active/20">
              <iframe
                src={`https://www.youtube.com/embed/${industry.videoId}?autoplay=1`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                title={`${industry.title} Video`}
              />
            </div>
          </div>
        </div>
      )}
    </main>
  )
}
